import { useCallback, useRef, useState } from 'react';

type UseControlledStateOptions<T> = {
  defaultValue: T;
  value?: T;
  onValueChange?: (value: T) => void;
};

/**
 * Unifying controlled and uncontrolled state handling.
 * Controlled mode is when `value` is not nullish.
 *
 * @example
 * // Controlled mode
 * const [someProp, setSomeProp] = useState(false);
 * const [isOpen, setIsOpen] = useControlledState({ value: someProp, onValueChange: setSomeProp });
 *
 * // Uncontrolled mode
 * const [isOpen, setIsOpen] = useControlledState({ defaultValue: false });
 */
const useControlledState = <T extends NonNullable<unknown>>({
  defaultValue,
  value,
  onValueChange,
}: UseControlledStateOptions<T>): [T, (newValue: T) => void] => {
  const isControlled = value != null;
  const initialIsControlled = useRef(isControlled);

  if (process.env.NODE_ENV !== 'production') {
    if (initialIsControlled.current && !isControlled) {
      throw new Error(
        'A component is changing a controlled state to be uncontrolled',
      );
    }

    if (!initialIsControlled.current && isControlled) {
      throw new Error(
        'A component is changing an uncontrolled state to be controlled',
      );
    }

    if (typeof value === 'function') {
      throw new Error('Function values are not supported');
    }
  }

  const [uncontrolledState, setUncontrolledState] = useState(defaultValue);

  const controlledOnValueChange = useCallback(
    (newValue: T) => {
      onValueChange?.(newValue);
    },
    [onValueChange],
  );

  const uncontrolledOnValueChange = useCallback(
    (newValue: T) => {
      setUncontrolledState(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange],
  );

  return isControlled
    ? ([value, controlledOnValueChange] as const)
    : ([uncontrolledState, uncontrolledOnValueChange] as const);
};

export default useControlledState;
